import FileSaver from 'file-saver'
import JSZip from 'jszip'
import Highcharts from 'highcharts'
import { exportReadabilityOptions } from 'modules/reports/components/charts/chart-export/ExportReadability'

export interface ChartExportItemTP {
    chart: Highcharts.Chart
    fileName: string
}

export type ChartZipRatioTP = 'full' | '16:9' | '4:3'

/* Escala aplicada na imagem gerada (mesma escala padrao do export do Highcharts). */
const IMAGE_SCALE = 2

type PngPixelSizeTP = {
    width: number,
    height: number,
}

/** Pixels do PNG: tamanho de origem vezes IMAGE_SCALE. */
function pngPixelSize(chartWidth: number, chartHeight: number): PngPixelSizeTP {
    return {
        width: chartWidth * IMAGE_SCALE,
        height: chartHeight * IMAGE_SCALE,
    }
}

/**
 * Tamanho de tela da proporcao escolhida no menu do ZIP.
 * Full usa a largura do elemento que envolve o grafico e altura 350.
 */
function zipSourceSize(chart: Highcharts.Chart, ratio: ChartZipRatioTP): PngPixelSizeTP {
    if (ratio === '16:9')
        return { width: 854, height: 480 }
    if (ratio === '4:3')
        return { width: 640, height: 480 }

    const host = chart.container.parentElement
    const hostWidth = host ? host.clientWidth : 0
    if (hostWidth <= 0)
        throw new Error('Largura Full indisponivel')

    return { width: hostWidth, height: 350 }
}

/**
 * Gera a imagem PNG (em memoria) de um grafico Highcharts, usando o mesmo SVG
 * produzido pelo botao de imagem nativo do Highcharts, renderizado num canvas.
 * O SVG sai no tamanho da proporcao pedida, nao no 600x400 padrao do Highcharts.
 */
export async function chartToPngBlob(chart: Highcharts.Chart, ratio: ChartZipRatioTP): Promise<Blob> {

    const source = zipSourceSize(chart, ratio)

    /* getSVGForExport eh adicionado pelo modulo "exporting" (ja carregado). */
    const svg = (chart as any).getSVGForExport({
        sourceWidth: source.width,
        sourceHeight: source.height,
    }, exportReadabilityOptions(chart)) as string

    const { width: canvasWidth, height: canvasHeight } = pngPixelSize(source.width, source.height)

    const blobUrl = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }))
    let image: HTMLImageElement
    try {
        image = await loadImage(blobUrl)
    } catch {
        image = await loadImage('data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg))
    } finally {
        URL.revokeObjectURL(blobUrl)
    }

    const canvas = document.createElement('canvas')
    canvas.width = canvasWidth
    canvas.height = canvasHeight

    const context = canvas.getContext('2d')
    if (!context)
        throw new Error('Nao foi possivel obter o contexto do canvas')

    /* Fundo branco para o PNG, igual ao export padrao do Highcharts. */
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, canvasWidth, canvasHeight)
    context.drawImage(image, 0, 0, canvasWidth, canvasHeight)

    return await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
            (blob) => blob ? resolve(blob) : reject(new Error('Falha ao gerar a imagem do grafico')),
            'image/png',
        )
    })
}

/**
 * Gera a imagem PNG de cada grafico e empacota todas num unico arquivo .zip.
 */
export async function downloadChartsAsZip(items: ChartExportItemTP[], zipName: string, ratio: ChartZipRatioTP): Promise<void> {

    const zip = new JSZip()
    const usedNames = new Set<string>()

    for (const item of items) {
        const blob = await chartToPngBlob(item.chart, ratio)
        zip.file(`${getUniqueFileName(item.fileName, usedNames)}.png`, blob)
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' })
    FileSaver.saveAs(zipBlob, zipName)
}

/**
 * Evita nomes de arquivo repetidos dentro do zip, adicionando um sufixo numerico.
 */
function getUniqueFileName(baseName: string, usedNames: Set<string>): string {

    let candidate = baseName
    let counter = 2
    while (usedNames.has(candidate)) {
        candidate = `${baseName}_${counter}`
        counter++
    }

    usedNames.add(candidate)
    return candidate
}

/**
 */
function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const image = new Image()
        image.onload = () => resolve(image)
        image.onerror = () => reject(new Error('Falha ao carregar a imagem do grafico'))
        image.src = src
    })
}
